/**
 * @file Registry-driven notification settings grouping and visibility helpers.
 */

import {
    EVENT_DEFINITIONS,
    EVENT_DEFINITIONS_BY_ID,
    GROUP_DEFINITIONS,
    NOTIFICATION_GROUPS,
    NOTIFICATION_SETTINGS_SURFACES,
    getNotificationEventLabel,
    isRetiredNotificationEvent,
} from "./notificationRegistryAdapter.js";

const FALLBACK_GROUP_ID = NOTIFICATION_GROUPS.ACTIVITY;
const LEGACY_CALENDAR_LEAVE_GROUP_IDS = new Set([
    NOTIFICATION_GROUPS.DIRECT,
    NOTIFICATION_GROUPS.SCHEDULE,
    NOTIFICATION_GROUPS.OPERATIONS,
]);
const LEGACY_GROUP_ORDER_MIGRATIONS = Object.freeze({
    [NOTIFICATION_GROUPS.OPERATIONS]: 50,
    [NOTIFICATION_GROUPS.DRUGS]: 60,
});

function toFiniteNumber(value, fallback) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
}

function getFallbackLabel(id) {
    return String(id || "")
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

function createUnknownDefinition(eventId) {
    return {
        id: eventId,
        label: getNotificationEventLabel(eventId),
        groupId: FALLBACK_GROUP_ID,
        order: 9999,
        settingsSurface: NOTIFICATION_SETTINGS_SURFACES.STANDARD,
        visibilityPermissions: [],
        visibilityRoles: [],
    };
}

function getDefaultEventLayout(definition) {
    return {
        groupId: definition.groupId,
        order: definition.order,
    };
}

function isDefaultLabel(groupConfig, group) {
    return !groupConfig.label || groupConfig.label === group.label;
}

function applyGroupOrderMigration(groupConfig, group) {
    const legacyOrder = LEGACY_GROUP_ORDER_MIGRATIONS[group.id];
    if (
        legacyOrder === undefined ||
        !isDefaultLabel(groupConfig, group) ||
        Number(groupConfig.order) !== legacyOrder
    ) {
        return groupConfig;
    }

    return {
        ...groupConfig,
        order: group.order,
    };
}

function shouldUseRegistryCalendarLeaveLayout(definition, eventLayout) {
    if (definition.groupId !== NOTIFICATION_GROUPS.CALENDAR_LEAVE) {
        return false;
    }

    const currentGroupId = eventLayout?.groupId;
    return !currentGroupId ||
        LEGACY_CALENDAR_LEAVE_GROUP_IDS.has(currentGroupId);
}

/**
 * Ensures notification layout config maps exist and include registry defaults.
 *
 * @param {object} configData Runtime or editable config object.
 * @param {boolean} [mutate=false] Whether to mutate the supplied config.
 * @returns {{notificationGroups: object, notificationEventLayout: object}} Layout config maps.
 */
export function getNotificationLayoutConfig(configData, mutate = false) {
    const source = configData || {};
    const notificationGroups = mutate
        ? (source.notificationGroups || {})
        : { ...(source.notificationGroups || {}) };
    const notificationEventLayout = mutate
        ? (source.notificationEventLayout || {})
        : { ...(source.notificationEventLayout || {}) };

    GROUP_DEFINITIONS.forEach((group) => {
        if (!notificationGroups[group.id]) {
            notificationGroups[group.id] = {
                label: group.label,
                order: group.order,
            };
            return;
        }

        notificationGroups[group.id] = applyGroupOrderMigration(
            notificationGroups[group.id],
            group,
        );
    });

    EVENT_DEFINITIONS.forEach((definition) => {
        const eventLayout = notificationEventLayout[definition.id];
        if (!eventLayout) {
            notificationEventLayout[definition.id] =
                getDefaultEventLayout(definition);
            return;
        }

        if (shouldUseRegistryCalendarLeaveLayout(definition, eventLayout)) {
            notificationEventLayout[definition.id] = {
                ...eventLayout,
                ...getDefaultEventLayout(definition),
            };
        }
    });

    if (mutate && configData) {
        configData.notificationGroups = notificationGroups;
        configData.notificationEventLayout = notificationEventLayout;
    }

    return {
        notificationGroups,
        notificationEventLayout,
    };
}

/**
 * Returns whether a notification event should be visible to the current user.
 *
 * @param {object} definition Registry event definition.
 * @param {object} userProfile Current user profile.
 * @returns {boolean} True when the event is relevant to the user.
 */
export function canUserSeeNotificationEvent(definition, userProfile = {}) {
    if (!definition) {return false;}

    const role = userProfile.role || "user";
    const requiredRoles = definition.visibilityRoles || [];
    if (requiredRoles.length > 0 && !requiredRoles.includes(role)) {
        return false;
    }

    const requiredPermissions = definition.visibilityPermissions || [];
    if (requiredPermissions.length === 0 || role === "admin") {
        return true;
    }

    const permissions = new Set(userProfile.permissions || []);
    return requiredPermissions.some((permission) => permissions.has(permission));
}

/**
 * Returns ordered notification group options for Settings layout controls.
 *
 * @param {object} configData Runtime or editable config object.
 * @returns {Array<{id: string, label: string, order: number}>} Group options.
 */
export function getNotificationGroupOptions(configData) {
    const { notificationGroups } = getNotificationLayoutConfig(configData);

    return Object.entries(notificationGroups)
        .map(([id, group]) => ({
            id,
            label: group.label || getFallbackLabel(id),
            order: toFiniteNumber(group.order, 9999),
        }))
        .sort((a, b) => {
            if (a.order !== b.order) {return a.order - b.order;}
            return a.label.localeCompare(b.label);
        });
}

/**
 * Builds ordered notification settings groups from registry and config.
 *
 * @param {object} configData Runtime or editable config object.
 * @param {object} [options] Grouping options.
 * @param {string} [options.surface] Settings surface to include.
 * @param {object|null} [options.controls] Admin controls map to filter by.
 * @param {object|null} [options.userProfile] Current user profile.
 * @returns {Array<object>} Ordered group view models.
 */
export function getNotificationSettingsGroups(configData, options = {}) {
    const {
        surface = NOTIFICATION_SETTINGS_SURFACES.STANDARD,
        controls = null,
        userProfile = null,
    } = options;
    const {
        notificationGroups,
        notificationEventLayout,
    } = getNotificationLayoutConfig(configData);
    const controlKeys = controls ? Object.keys(controls) : [];
    const eventIds = controlKeys.length > 0
        ? controlKeys
        : EVENT_DEFINITIONS.map((definition) => definition.id);
    const groupBuckets = new Map();

    eventIds.forEach((eventId, index) => {
        if (isRetiredNotificationEvent(eventId)) {return;}

        const definition = EVENT_DEFINITIONS_BY_ID[eventId] ||
            createUnknownDefinition(eventId);
        if (definition.settingsSurface !== surface) {return;}
        if (userProfile && !canUserSeeNotificationEvent(definition, userProfile)) {
            return;
        }

        const eventLayout = notificationEventLayout[eventId] || {};
        const groupId = eventLayout.groupId || definition.groupId ||
            FALLBACK_GROUP_ID;
        const groupConfig = notificationGroups[groupId] || {};
        const registryGroup = GROUP_DEFINITIONS.find((group) =>
            group.id === groupId
        );
        const groupLabel = groupConfig.label ||
            registryGroup?.label ||
            getFallbackLabel(groupId);
        const groupOrder = toFiniteNumber(
            groupConfig.order,
            registryGroup?.order ?? 9999,
        );

        if (!groupBuckets.has(groupId)) {
            groupBuckets.set(groupId, {
                id: groupId,
                label: groupLabel,
                order: groupOrder,
                events: [],
            });
        }

        groupBuckets.get(groupId).events.push({
            id: eventId,
            label: definition.label || getNotificationEventLabel(eventId),
            order: toFiniteNumber(eventLayout.order, definition.order ?? index),
            definition,
        });
    });

    return [...groupBuckets.values()]
        .map((group) => ({
            ...group,
            events: group.events.sort((a, b) => {
                if (a.order !== b.order) {return a.order - b.order;}
                return a.label.localeCompare(b.label);
            }),
        }))
        .filter((group) => group.events.length > 0)
        .sort((a, b) => {
            if (a.order !== b.order) {return a.order - b.order;}
            return a.label.localeCompare(b.label);
        });
}

/**
 * Returns ordered timing events that support user/admin day controls.
 *
 * @param {object} configData Runtime or editable config object.
 * @param {object} controls Admin controls map.
 * @param {object} userProfile Current user profile.
 * @returns {Array<object>} Ordered timing events.
 */
export function getNotificationTimingEvents(
    configData,
    controls,
    userProfile = {},
) {
    return getNotificationSettingsGroups(configData, {
        controls,
        userProfile,
    })
        .flatMap((group) => group.events)
        .filter((event) => controls?.[event.id]?.days !== undefined);
}

export { NOTIFICATION_SETTINGS_SURFACES };
